import { useState, useEffect } from "react";
import Api from "../../../../shared/api/api";
import "../styles/AdminSocialPosts.css";
import { DateTime } from "luxon";

// Convert local PST datetime to UTC (for backend)
function convertToUtc(localDatetime) {
  if (!localDatetime) return null;
  return DateTime.fromISO(localDatetime, { zone: "America/Los_Angeles" })
    .toUTC()
    .toISO({ suppressMilliseconds: true });
}

// Convert UTC from DB back to PST (for display if needed)
function convertToPst(utcDatetime) {
  if (!utcDatetime) return null;
  return DateTime.fromISO(utcDatetime, { zone: "utc" })
    .setZone("America/Los_Angeles")
    .toFormat("yyyy-MM-dd'T'HH:mm");
}

// Convert UTC ISO string to "MM/dd/yyyy at hh:mm a" in PST
function formatScheduledTime(utcDatetime) {
  if (!utcDatetime) return "—";
  return DateTime.fromISO(utcDatetime, { zone: "utc" })
    .setZone("America/Los_Angeles")
    .toFormat("MM/dd/yyyy 'at' hh:mm a");
}


export default function SocialPosts() {
  const [posts, setPosts] = useState([]);
  const [showModal, setShowModal] = useState(false);

  const [fbBody, setFbBody] = useState("");
  const [fbImage, setFbImage] = useState(null);
  const [fbScheduledAt, setFbScheduledAt] = useState("");
  const [fbFirstComment, setFbFirstComment] = useState("");

  const [igBody, setIgBody] = useState("");
  const [igImage, setIgImage] = useState(null);
  const [igScheduledAt, setIgScheduledAt] = useState("");
  const [igFirstComment, setIgFirstComment] = useState("");

  const [twBody, setTwBody] = useState("");
  const [twScheduledAt, setTwScheduledAt] = useState("");
  const [twFirstComment, setTwFirstComment] = useState("");

  const [loadingIds, setLoadingIds] = useState([]);

  useEffect(() => {
    fetchPosts();
  }, []);

  const fetchPosts = async () => {
    try {
      const data = await Api.getAllAuthor("social-posts");
      const postArray = (data.posts || data).sort((a, b) => {
        if (!a.post_id && b.post_id) return -1;
        if (a.post_id && !b.post_id) return 1;
        return new Date(b.updated_at) - new Date(a.updated_at);
      });
      setPosts(postArray);
    } catch (err) {
      console.error("Error fetching social posts:", err);
    }
  };

  const handleImageChange = (e, setImage) => {
    setImage(e.target.files[0]);
  };

  const uploadImage = async (imageFile, content) => {
    if (imageFile instanceof File) {
      const imgForm = new FormData();
      imgForm.append("imageFile", imageFile);
      imgForm.append("related_type", "social");
      imgForm.append("related_id", content.slice(0, 30));
      imgForm.append("alt_text", content.slice(0, 30));
      imgForm.append("is_primary", "true");

      const res = await Api.uploadImage(imgForm);

      if (res.url) {
        const parts = res.url.split("/");
        const fileName = parts[parts.length - 1];
        return `https://www.kennethlpowell.com/images/${fileName}`;
      }
    }
    return null;
  };

  const handleSubmit = async () => {
    try {
      if (fbBody.trim()) {
        const fbImageUrl = await uploadImage(fbImage, fbBody);
        await Api.createSocialPost({
          content: fbBody,
          image: fbImageUrl,
          hashtags: "",
          tags: "",
          platform: "facebook",
          scheduled_at: convertToUtc(fbScheduledAt) || null,
          first_comment: fbFirstComment || null,
        });
      }

      if (igBody.trim()) {
        const igImageUrl = await uploadImage(igImage, igBody);
        await Api.createSocialPost({
          content: igBody,
          image: igImageUrl,
          hashtags: "",
          tags: "",
          platform: "instagram",
          scheduled_at: convertToUtc(igScheduledAt) || null,
          first_comment: igFirstComment || null,
        });
      }

      if (twBody.trim()) {
        await Api.createSocialPost({
          content: twBody,
          image: null,
          hashtags: "",
          tags: "",
          platform: "twitter",
          scheduled_at: convertToUtc(twScheduledAt) || null,
        });
      }

      await fetchPosts();
      closeModal();
    } catch (err) {
      console.error("Error submitting posts:", err);
    }
  };

  const closeModal = () => {
    setShowModal(false);
    setFbBody("");
    setFbImage(null);
    setFbScheduledAt("");
    setFbFirstComment("");
    setIgBody("");
    setIgImage(null);
    setIgScheduledAt("");
    setIgFirstComment("");
    setTwBody("");
    setTwScheduledAt("");
    setTwFirstComment("");
  };

  const handlePostNow = async (id, platform) => {
    setLoadingIds((prev) => [...prev, id]);
    try {
      if (platform === "facebook") {
        await Api.postToFacebook(id);
      } else if (platform === "instagram") {
        await Api.postToInstagram(id);
      } else if (platform === "twitter") {
        await Api.postToTwitter(id);
      }
      await fetchPosts();
    } catch (err) {
      console.error("Error posting now:", err);
    } finally {
      setLoadingIds((prev) => prev.filter((pid) => pid !== id));
    }
  };

  return (
    <div className="adminPageContainer">
      <h1 className="adminSocialPostTitle">Admin Social Posts</h1>
      <button className="addPostBtn" onClick={() => setShowModal(true)}>
        Add Post
      </button>

      {showModal && (
        <div className="modalOverlay">
          <div className="modalContent">
            <h2>Add Social Posts</h2>
            <div className="platformsContainer">
              {/* Facebook */}
              <div className="formSection">
                <h3>Facebook Body</h3>
                <textarea value={fbBody} onChange={(e) => setFbBody(e.target.value)} />
                <input type="file" onChange={(e) => handleImageChange(e, setFbImage)} />
                <input
                  type="datetime-local"
                  value={fbScheduledAt}
                  onChange={(e) => setFbScheduledAt(e.target.value)}
                />
                <input
                  type="text"
                  placeholder="Optional first comment or link"
                  value={fbFirstComment}
                  onChange={(e) => setFbFirstComment(e.target.value)}
                />
              </div>

              {/* Instagram */}
              <div className="formSection">
                <h3>Instagram Body</h3>
                <textarea value={igBody} onChange={(e) => setIgBody(e.target.value)} />
                <input type="file" onChange={(e) => handleImageChange(e, setIgImage)} />
                <input
                  type="datetime-local"
                  value={igScheduledAt}
                  onChange={(e) => setIgScheduledAt(e.target.value)}
                />
                <input
                  type="text"
                  placeholder="Optional first comment or link"
                  value={igFirstComment}
                  onChange={(e) => setIgFirstComment(e.target.value)}
                />
              </div>

              {/* Twitter */}
              <div className="formSection">
                <h3>Twitter Body</h3>
                <textarea
                  value={twBody}
                  onChange={(e) => setTwBody(e.target.value)}
                  maxLength="280"
                />
                <p>{twBody.length} / 280</p>
                <input
                  type="datetime-local"
                  value={twScheduledAt}
                  onChange={(e) => setTwScheduledAt(e.target.value)}
                />
              </div>
            </div>

            <div className="buttonRow">
              <button className="publishingSubmitBtn" onClick={handleSubmit}>Post</button>
              <button className="cancelBtn" onClick={closeModal}>Cancel</button>
            </div>
          </div>
        </div>
      )}

      <div className="adminPostsList">
        {posts.map((p) => (
          <div className="adminPostRow" key={p.id}>
            {p.platform !== "twitter" && (
              <img
                src={p.image_path ? `${p.image_path}${p.image_path.includes("?") ? "&raw=1" : "?raw=1"}` : "https://via.placeholder.com/150x100?text=No+Image"}
                alt={p.content?.slice(0, 30) || ""}
                className="adminPostThumb"
              />
            )}
            <div className="adminPostInfo">
              <h3>{p.content?.slice(0, 60)}{p.content?.length > 60 ? "..." : ""}</h3>
              <p><strong>Status:</strong> {p.status || "N/A"}</p>
              <p><strong>Platform:</strong> {p.platform}</p>
              <p><strong>Scheduled:</strong> {formatScheduledTime(p.scheduled_at) || "—"}</p>
              <p><strong>First Comment:</strong> {p.first_comment || "—"}</p>
              <p><strong>Comment Posted:</strong> {p.first_comment_posted ? "✅ Yes" : "❌ No"}</p>
              <button
                className={p.post_id ? "submittedAndDisabled":"submitBtn"}
                onClick={() => handlePostNow(p.id, p.platform)}
                disabled={loadingIds.includes(p.id) || !!p.post_id}
              >
                {p.post_id ? "Already Posted" : loadingIds.includes(p.id) ? "Posting..." : `Post to ${p.platform.charAt(0).toUpperCase() + p.platform.slice(1)}`}
              </button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
